import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { KOD_POBRANIE_NOWY, kodZnany, nazwaOperacji, podzielOperacje } from '@/lib/kody-operacji';
import { fetchSlownikOperacji } from '@/lib/operations-api';

/**
 * UPRAWNIENIA ZAZNACZANE OD RAZU PRZY ZAKŁADANIU GRUPY.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „dodaj od razu przy dodawaniu grupy, żeby można było zaznaczyć
 *  uprawnienia". Do tej pory grupa powstawała bez nich, a operacje nadawało się w DRUGIM oknie,
 *  po zapisaniu — czyli grupa istniała chwilę bez ani jednego uprawnienia i trafiała w tym
 *  stanie do bazy klienta.
 *
 *  ⚠️⚠️ TEN SAM KSZTAŁT CO OKNO EDYCJI UPRAWNIEŃ (zamówione tego samego dnia: „żeby było dokładnie
 *  jak w edycji uprawnień — 4 podstawowe u góry i dodatkowe w osobnej liście"). Pierwsza wersja
 *  była płaską, przewijaną siatką wszystkich 49 kodów — czyli dokładnie tym, na co przyszło
 *  zgłoszenie o oknie edycji miesiąc wcześniej („trzeba scrollować po te podstawowe"). Podział
 *  bierzemy z `podzielOperacje`, WSPÓLNEJ reguły, a nie z drugiej kopii tej samej czwórki.
 *
 *  ⚠️ RÓŻNI SIĘ JEDNYM SŁOWEM I TO JEST ŚWIADOME: tam nagłówek mówi „nadanych", tu „zaznaczono".
 *  W formularzu, który jeszcze nie został zapisany, nic nie jest nadane.
 *
 *  ⚠️ JEDNO ŻĄDANIE, NIE DWA: `POST /employee-groups` przyjmuje `operacje` (dodane przy pracach
 *  nad Vending+). Zakładanie grupy i nadanie uprawnień dwoma żądaniami dałoby stan połowiczny
 *  przy każdym błędzie drugiego — grupę bez uprawnień, o której nikt nie wie, że jest niepełna.
 *
 *  ⚠️ SŁOWNIK POCHODZI Z BAZY KLIENTA (`Transactions`), więc jest PER INSTALACJA i bywa pusty,
 *  dopóki agent nie przyśle konfiguracji. Pusty słownik mówi to wprost, zamiast pokazywać naszą
 *  własną listę kodów, których ta instalacja może nie znać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function WyborOperacjiGrupy({
  groupId,
  wybrane,
  onZmiana,
}: {
  groupId: string;
  wybrane: string[];
  onZmiana: (kody: string[]) => void;
}) {
  const { t } = useI18n();
  /** Dodatkowe zwinięte domyślnie — tak samo jak w oknie edycji; licznik obok mówi, ile w środku. */
  const [dodatkoweOtwarte, setDodatkoweOtwarte] = useState(false);
  const q = useQuery({
    queryKey: ['employee-groups', 'operations', 'slownik', groupId],
    queryFn: () => fetchSlownikOperacji(groupId),
    /** Słownik zmienia się z konfiguracją klienta, nie w trakcie wypełniania okna. */
    staleTime: 5 * 60_000,
  });

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  DOMYŚLNY PTASZEK NA „POBRANIE — ARTYKUŁ NOWY" (zamówione 04.09.2026).
   *
   *  ⚠️⚠️ TYLKO GDY SŁOWNIK WYSPY ZNA TEN KOD. API odmawia założenia grupy z kodem poza tabelą
   *  `Transactions` („Ta instalacja SPS nie zna kodów operacji: …"), więc ptaszek wstawiony na
   *  ślepo wywalałby ZAKŁADANIE GRUPY na instalacji bez `WN` — awaria w miejscu, w którym
   *  wcześniej wszystko działało, i to z powodu udogodnienia.
   *
   *  ⚠️⚠️ RAZ, NIE PRZY KAŻDYM PRZEBIEGU. Warunek „jeśli nic nie zaznaczono, zaznacz WN" wyglądałby
   *  krócej i BIŁBY SIĘ Z CZŁOWIEKIEM: odznaczenie ostatniej pozycji natychmiast wracałoby jako
   *  ptaszek i nie dałoby się założyć grupy bez tego uprawnienia. Dlatego jednorazowy znacznik.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const domyslneWstawione = useRef(false);
  useEffect(() => {
    if (domyslneWstawione.current || q.data === undefined) return;
    domyslneWstawione.current = true;
    if (wybrane.length > 0 || !q.data.includes(KOD_POBRANIE_NOWY)) return;
    onZmiana([KOD_POBRANIE_NOWY]);
  }, [q.data, wybrane, onZmiana]);

  const przelacz = (code: string) =>
    onZmiana(wybrane.includes(code) ? wybrane.filter((k) => k !== code) : [...wybrane, code]);

  /**
   * ⚠️ SORTUJEMY PO NAZWIE, alfabetem polskim — tak samo jak okno edycji. Człowiek szuka tu
   * CZYNNOŚCI, nie trzyliterowego skrótu, a bez `localeCompare` „Ł" i „Ż" lądują za „Z".
   */
  const kody = useMemo(
    () => [...(q.data ?? [])].sort((a, b) => nazwaOperacji(t, a).localeCompare(nazwaOperacji(t, b), 'pl')),
    [q.data, t],
  );
  const { podstawowe, dodatkowe } = useMemo(() => podzielOperacje(kody, (k) => k), [kody]);
  const dodatkoweCzynne = dodatkowe.filter((c) => wybrane.includes(c)).length;

  if (q.isPending) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        {t('common.loading')}
      </p>
    );
  }

  if (kody.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('grp.opsBrakSlownika')}</p>;
  }

  /**
   * JEDEN WIERSZ — ten sam kształt co w oknie edycji: kod w kolumnie o stałej szerokości, nazwa
   * obok. Kody mają dwa albo trzy znaki, więc bez tej kolumny początki opisów rozjeżdżają się
   * o jeden znak i lista traci krawędź, po której da się przejechać wzrokiem.
   */
  const wiersz = (code: string) => (
    <label
      key={code}
      className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm leading-tight hover:bg-accent/50"
      title={kodZnany(code) ? undefined : t('ops.unnamedHint')}
    >
      <input
        type="checkbox"
        className="size-3.5 shrink-0 accent-[var(--primary)]"
        checked={wybrane.includes(code)}
        onChange={() => przelacz(code)}
      />
      <span className="w-8 shrink-0 font-mono text-[11px] lowercase text-muted-foreground">{code}</span>
      {/*
        ⚠️ NAZWA TYLKO POTWIERDZONA — `nazwaOperacji` oddaje SAM KOD, gdy znaczenia nie
        potwierdziliśmy (tabela `Transactions` w SPS ma jedną kolumnę i ani jednego opisu).
        Wymyślona nazwa byłaby zgadywaniem podanym jako fakt komuś, kto na tej podstawie nadaje
        uprawnienie; taki wiersz tłumaczy się dymkiem `ops.unnamedHint`, dokładnie jak w edycji.
      */}
      <span className="min-w-0 flex-1 truncate">{nazwaOperacji(t, code)}</span>
    </label>
  );

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {t('grp.opsWybrane').replace('{ile}', String(wybrane.length)).replace('{z}', String(kody.length))}
      </p>

      {/*
        ⚠️ PODSTAWOWE BEZ PRZEWIJANIA I BEZ SORTOWANIA ALFABETYCZNEGO — cztery wiersze widać naraz,
        w kolejności, w jakiej się o nich myśli (wydawanie → uzupełnianie → korekta stanów → zwrot).
      */}
      {podstawowe.length > 0 && <div className="rounded-md border border-border p-1">{podstawowe.map(wiersz)}</div>}

      {/*
        ⚠️ LICZNIK NA ZWINIĘTYM NAGŁÓWKU, ŻEBY NIE TRZEBA BYŁO ROZWIJAĆ. Zwinięta sekcja bez licznika
        kazałaby ją otwierać za każdym razem tylko po to, żeby sprawdzić, czy pod spodem czegoś nie
        ma — a to jest pytanie, które zadaje się ZAWSZE.
      */}
      {dodatkowe.length > 0 && (
        <div className="rounded-md border border-border">
          <button
            type="button"
            onClick={() => setDodatkoweOtwarte((v) => !v)}
            aria-expanded={dodatkoweOtwarte}
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/50"
          >
            {dodatkoweOtwarte ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            <span className="font-medium">{t('ops.extra')}</span>
            <span className={dodatkoweCzynne > 0 ? 'text-foreground' : undefined}>
              {t('ops.extraCount')
                .replace('{n}', String(dodatkoweCzynne))
                .replace('{total}', String(dodatkowe.length))}
            </span>
          </button>
          {dodatkoweOtwarte && (
            <div className="max-h-72 overflow-y-auto border-t border-border p-1">{dodatkowe.map(wiersz)}</div>
          )}
        </div>
      )}
    </div>
  );
}
